import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

/**
 * The chart forms the insights page needs.
 *
 * Rules that are not negotiable here, and why:
 *
 * *One axis per chart.* Percent and degrees never share a scale, so relative
 * humidity gets its own chart rather than a second y-axis on the temperature
 * one. A dual axis lets the author place the crossing point anywhere, which
 * means the reader cannot trust it. Where two measures belong together (dew
 * point and duty by hour) they are stacked as small multiples on one x-axis.
 *
 * *Colour follows the entity, not the rank.* Indoor is always slot 1, outdoor
 * always slot 2, setpoint always slot 3, in every chart on the page. Filtering
 * to one zone must not repaint what is left. Anything that is not one of those
 * entities (cooling activity, reference bands) is drawn in neutral ink.
 *
 * *Only tokens that exist.* Every colour below is a variable defined in
 * styles.css. An undefined `var()` in an SVG fill silently resolves to black,
 * which is how half this page once became unreadable in dark mode.
 *
 * *Time is a scale, not an index.* Points sit at their timestamps, so an outage
 * shows as a gap instead of being squeezed out of the picture.
 *
 * *Identity is never colour alone.* Every chart carries a legend and a table
 * view, so the series can be told apart without seeing hue at all.
 */

// ---------------------------------------------------------------- plumbing

/**
 * Live pixel width, so labels are typeset rather than scaled by a viewBox.
 *
 * Measured in a layout effect and re-measured whenever `key` changes, because
 * these charts render an empty state on their first pass — before the data
 * arrives there is no element to observe, and an effect that only runs on mount
 * would attach the observer to nothing and leave the chart drawing itself at
 * the fallback width for the rest of the session.
 */
function useWidth<T extends HTMLElement>(ref: React.RefObject<T>, key: unknown) {
  const [w, setW] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => setW(el.getBoundingClientRect().width)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref, key])
  return w
}

/** Axis ticks on round numbers, at most `count` of them. */
function niceTicks(lo: number, hi: number, count = 5): number[] {
  if (!isFinite(lo) || !isFinite(hi) || hi <= lo) return [lo]
  const raw = (hi - lo) / count
  const mag = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10
  const out: number[] = []
  for (let v = Math.ceil(lo / step - 1e-9) * step; v <= hi + 1e-9; v += step) {
    out.push(Math.round(v / step) * step)
  }
  return out
}

/** Decimals a tick label needs so neighbouring ticks never print the same. */
function tickDigits(ticks: number[]) {
  if (ticks.length < 2) return 1
  const step = Math.abs(ticks[1] - ticks[0])
  return step >= 1 ? 0 : step >= 0.1 ? 1 : 2
}

const HOUR = 3_600_000

/**
 * Time ticks on local clock boundaries — midnight, or every 1/2/3/6/12 hours —
 * rather than at whatever instant the data happened to start.
 */
function timeTicks(t0: number, t1: number, max = 6) {
  const span = t1 - t0
  const hours = [1, 2, 3, 6, 12]
  const hStep = hours.find((h) => span / (h * HOUR) <= max)
  const out: number[] = []
  if (hStep) {
    const d = new Date(t0)
    d.setMinutes(0, 0, 0)
    d.setHours(Math.floor(d.getHours() / hStep) * hStep)
    for (let t = d.getTime(); t <= t1; t += hStep * HOUR) if (t >= t0) out.push(t)
    return { ticks: out, daily: false }
  }
  const days = [1, 2, 7, 14, 30, 60].find((n) => span / (n * 24 * HOUR) <= max) ?? 90
  const d = new Date(t0)
  d.setHours(0, 0, 0, 0)
  // Stepped by calendar date, not by 24h, so a DST change cannot walk the
  // ticks off midnight.
  for (; d.getTime() <= t1; d.setDate(d.getDate() + days)) {
    if (d.getTime() >= t0) out.push(d.getTime())
  }
  return { ticks: out, daily: true }
}

function timeLabel(t: number, daily: boolean) {
  const d = new Date(t)
  if (daily || (d.getHours() === 0 && d.getMinutes() === 0)) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }
  return d.toLocaleTimeString(undefined, { hour: 'numeric' })
}

const clock = (t: string | number) => new Date(t).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

export const hhmm = (m: number) =>
  m >= 60 ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}m` : `${Math.round(m)}m`

/** Converts a temperature *difference*: ratio only, never the +32 offset. */
const deltaConv = (deg: string) => (v: number) => (deg === '°F' ? v * 1.8 : v)

/** Tooltip that stays inside the plot, flipping to the left of the pointer. */
function Tip({ x, w, top = 8, children }: {
  x: number; w: number; top?: number; children: ReactNode
}) {
  const TW = 210
  const left = x + 14 + TW > w ? Math.max(4, x - 14 - TW) : x + 14
  return <div className="tip" style={{ left, top }}>{children}</div>
}

function TipRow({ sw, label, value }: { sw?: string; label: string; value: string }) {
  return (
    <div className="tiprow">
      <i className={`sw ${sw ?? 'bare'}`} /><span>{label}</span><b>{value}</b>
    </div>
  )
}

/** Title, caption, legend and the table twin every chart carries. */
function Card({ title, caption, legend, table, children }: {
  title: string
  caption?: ReactNode
  legend?: ReactNode
  /** A function, so a hidden 400-row table costs nothing to render. */
  table?: () => ReactNode
  children: ReactNode
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="chart">
      <div className="chead">
        <h2>{title}</h2>
        {table && (
          <button className="btn ghost sm" onClick={() => setShow(!show)}
                  aria-expanded={show}>
            {show ? 'Hide table' : 'Table'}
          </button>
        )}
      </div>
      {caption && <p className="cap">{caption}</p>}
      {legend && <div className="legend">{legend}</div>}
      {children}
      {show && table && <div className="scroll tv">{table()}</div>}
    </div>
  )
}

function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="chart">
      <h2>{title}</h2>
      <div className="empty">{children ?? 'Nothing logged in this range.'}</div>
    </div>
  )
}

/** Horizontal gridlines and their labels, shared by every value axis. */
function YGrid({ ticks, y, x0, x1, fmt }: {
  ticks: number[]; y: (v: number) => number; x0: number; x1: number
  fmt: (v: number) => string
}) {
  return (
    <>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={x0} x2={x1} y1={y(v)} y2={y(v)} stroke="var(--border)" strokeWidth={1} />
          <text x={x0 - 8} y={y(v) + 4} textAnchor="end" className="tick">{fmt(v)}</text>
        </g>
      ))}
    </>
  )
}

/** Arrow keys walk the hover through the points; the tooltip follows focus. */
function keyNav(n: number, hover: number | null, set: (i: number | null) => void) {
  return {
    tabIndex: 0,
    onFocus: () => set(hover ?? n - 1),
    onBlur: () => set(null),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()
        const d = e.key === 'ArrowLeft' ? -1 : 1
        set(Math.max(0, Math.min(n - 1, (hover ?? n - 1) + d)))
      } else if (e.key === 'Escape') set(null)
    },
  }
}

// ---------------------------------------------------------------- line

export interface Series {
  key: string
  label: string
  slot: 1 | 2 | 3
  /** A held value (a setpoint) is a step, not a slope between samples. */
  step?: boolean
}
type Row = Record<string, number | null> & { t: string }

interface LineProps {
  rows: Row[]
  series: Series[]
  /** Appended to every value — "°" or "%". One unit, because one axis. */
  unit: string
  /** Converts the stored value for display (Celsius → the chosen unit). */
  conv?: (v: number) => number
  digits?: number
  height?: number
  caption?: string
  title: string
  /** Bucket width in seconds, so a missing bucket reads as a gap. */
  bucketS?: number
  /** Draw the `cool` share of each bucket as a strip under the plot. */
  activity?: boolean
}

export function LineChart({
  rows, series, unit, conv = (v) => v, digits = 1, height = 220, caption, title,
  bucketS, activity,
}: LineProps) {
  const box = useRef<HTMLDivElement>(null)
  const w = useWidth(box, rows.length)
  const [hover, setHover] = useState<number | null>(null)

  const vals = rows.flatMap((r) => series.map((s) => r[s.key])
    .filter((v): v is number => v != null).map(conv))
  if (!rows.length || !vals.length) return <Empty title={title} />

  const ts = rows.map((r) => new Date(r.t).getTime())
  const bucket = (bucketS ?? 0) * 1000
    || (ts.length > 1 ? (ts[ts.length - 1] - ts[0]) / (ts.length - 1) : HOUR)
  const t0 = ts[0]
  const t1 = Math.max(ts[ts.length - 1], t0 + bucket)

  const strip = activity && rows.some((r) => (r.cool ?? 0) > 0)
  const pad = { l: 48, r: 56, t: 12, b: strip ? 40 : 26 }
  const iw = Math.max(80, w - pad.l - pad.r)
  const ih = height - pad.t - pad.b

  const lo = Math.min(...vals)
  const hi = Math.max(...vals)
  const padY = Math.max(0.5, (hi - lo) * 0.12)
  const ticks = niceTicks(lo - padY, hi + padY)
  const y0 = Math.min(lo - padY, ticks[0])
  const y1 = Math.max(hi + padY, ticks[ticks.length - 1])
  const dg = tickDigits(ticks)

  const x = (t: number) => pad.l + ((t - t0) / (t1 - t0 || 1)) * iw
  const yv = (v: number) => pad.t + ih - ((v - y0) / (y1 - y0 || 1)) * ih
  const y = (v: number) => yv(conv(v))

  /** Breaks the path at gaps: a straight line across an outage is a lie. */
  const path = (s: Series) => {
    let d = ''
    let prev: number | null = null
    rows.forEach((r, i) => {
      const v = r[s.key]
      if (v == null) { prev = null; return }
      const gap = prev != null && ts[i] - ts[prev] > bucket * 2.5
      if (prev == null || gap) d += `M${x(ts[i]).toFixed(1)} ${y(v).toFixed(1)} `
      else if (s.step) d += `H${x(ts[i]).toFixed(1)} V${y(v).toFixed(1)} `
      else d += `L${x(ts[i]).toFixed(1)} ${y(v).toFixed(1)} `
      prev = i
    })
    return d.trim()
  }

  // End labels only when none collide. Nudged labels detach from their lines,
  // so on collision the legend and tooltip carry identity instead.
  const ends = series.map((s) => {
    let i = rows.length - 1
    while (i >= 0 && rows[i][s.key] == null) i--
    return i < 0 ? null : { s, i, v: rows[i][s.key] as number }
  }).filter(Boolean) as { s: Series; i: number; v: number }[]
  const ys = ends.map((e) => y(e.v)).sort((a, b) => a - b)
  const labelEnds = ys.every((v, k) => k === 0 || v - ys[k - 1] >= 14)

  const { ticks: xt, daily } = timeTicks(t0, t1)
  const at = hover != null ? rows[hover] : null
  const fmt = (v: number | null | undefined) =>
    v == null ? '—' : `${conv(v).toFixed(digits)}${unit}`

  const nearest = (px: number) => {
    const t = t0 + ((px - pad.l) / iw) * (t1 - t0)
    let a = 0, b = ts.length - 1
    while (b - a > 1) { const m = (a + b) >> 1; if (ts[m] < t) a = m; else b = m }
    return Math.abs(ts[a] - t) <= Math.abs(ts[b] - t) ? a : b
  }

  return (
    <Card title={title} caption={caption}
      legend={<>
        {series.map((s) => (
          <span key={s.key}><i className={`sw s${s.slot}`} />{s.label}</span>
        ))}
        {strip && <span><i className="sw act" />Cooling (share of interval)</span>}
      </>}
      table={() => (
        <table>
          <thead><tr>
            <th>Time</th>
            {series.map((s) => <th key={s.key} className="num">{s.label}</th>)}
            {strip && <th className="num">Cooling</th>}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="muted">{clock(r.t)}</td>
                {series.map((s) => <td key={s.key} className="num">{fmt(r[s.key])}</td>)}
                {strip && <td className="num">{Math.round(100 * (r.cool ?? 0))}%</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}>
      <div ref={box} className="plot">
        <svg width="100%" height={height} role="img" className="focusable"
             aria-label={`${title}. ${series.map((s) => s.label).join(', ')}. `
               + 'Use the arrow keys to step through the points.'}
             {...keyNav(rows.length, hover, setHover)}
             onMouseLeave={() => setHover(null)}
             onMouseMove={(e) => {
               const r = e.currentTarget.getBoundingClientRect()
               setHover(nearest(e.clientX - r.left))
             }}>
          <YGrid ticks={ticks} y={yv} x0={pad.l} x1={pad.l + iw}
                 fmt={(v) => `${v.toFixed(dg)}${unit}`} />

          {xt.map((t) => (
            <text key={t} x={x(t)} y={height - 8} textAnchor="middle" className="tick">
              {timeLabel(t, daily)}
            </text>
          ))}

          {strip && (
            <g>
              <text x={pad.l - 8} y={pad.t + ih + 13} textAnchor="end" className="tick">
                cooling
              </text>
              <rect x={pad.l} y={pad.t + ih + 6} width={iw} height={8} rx={2}
                    fill="var(--surface-hi)" />
              {rows.map((r, i) => (r.cool ?? 0) > 0 && (
                <rect key={i} x={x(ts[i])} y={pad.t + ih + 6}
                      width={Math.max(1, x(ts[i] + bucket) - x(ts[i]))} height={8}
                      fill="var(--text)" opacity={0.15 + 0.6 * (r.cool as number)} />
              ))}
            </g>
          )}

          {series.map((s) => (
            <path key={s.key} d={path(s)} fill="none"
                  stroke={`var(--s${s.slot})`} strokeWidth={2}
                  strokeLinecap="round" strokeLinejoin="round" />
          ))}

          {ends.map((e) => (
            <g key={e.s.key}>
              <circle cx={x(ts[e.i])} cy={y(e.v)} r={4} fill={`var(--s${e.s.slot})`}
                      stroke="var(--surface)" strokeWidth={2} />
              {labelEnds && (
                <text x={x(ts[e.i]) + 9} y={y(e.v) + 4} className="dlabel">
                  {conv(e.v).toFixed(digits)}{unit}
                </text>
              )}
            </g>
          ))}

          {hover != null && at && (
            <>
              <line x1={x(ts[hover])} x2={x(ts[hover])} y1={pad.t} y2={pad.t + ih}
                    stroke="var(--border-hi)" strokeWidth={1} />
              {series.map((s) => at[s.key] != null && (
                <circle key={s.key} cx={x(ts[hover])} cy={y(at[s.key] as number)} r={4}
                        fill={`var(--s${s.slot})`} stroke="var(--surface)" strokeWidth={2} />
              ))}
            </>
          )}
        </svg>

        {hover != null && at && (
          <Tip x={x(ts[hover])} w={w} top={pad.t}>
            <div className="muted">{clock(at.t)}</div>
            {series.map((s) => (
              <TipRow key={s.key} sw={`s${s.slot}`} label={s.label} value={fmt(at[s.key])} />
            ))}
            {strip && (
              <TipRow sw="act" label="Cooling" value={`${Math.round(100 * (at.cool ?? 0))}%`} />
            )}
          </Tip>
        )}
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------- runtime

export interface DailyRow {
  day: string
  cool: number
  heat: number
  fan: number
  on_min: number
}

/** A column with a rounded data end and a square foot on the baseline. */
function column(x: number, top: number, w: number, h: number, r = 3) {
  if (h <= 0) return ''
  const rr = Math.min(r, w / 2, h)
  return `M${x} ${top + h} V${top + rr} Q${x} ${top} ${x + rr} ${top} `
    + `H${x + w - rr} Q${x + w} ${top} ${x + w} ${top + rr} V${top + h} Z`
}

/**
 * Runtime per local day.
 *
 * Columns, not a line: each bar is a discrete day's total, and a line between
 * them would imply values in between that do not exist.
 */
export function RuntimeChart({ rows, height = 220 }: { rows: DailyRow[]; height?: number }) {
  const box = useRef<HTMLDivElement>(null)
  const w = useWidth(box, rows.length)
  const [hover, setHover] = useState<number | null>(null)

  if (!rows.length) return <Empty title="Runtime per day" />

  const pad = { l: 52, r: 14, t: 12, b: 26 }
  const iw = Math.max(80, w - pad.l - pad.r)
  const ih = height - pad.t - pad.b
  const hi = Math.max(...rows.map((r) => r.cool + r.heat), 1)
  const ticks = niceTicks(0, hi)
  const top = Math.max(ticks[ticks.length - 1], hi)

  const slot = iw / rows.length
  const bw = Math.max(3, Math.min(24, slot * 0.6))
  const cx = (i: number) => pad.l + slot * i + slot / 2
  const yOf = (v: number) => pad.t + ih - (v / top) * ih
  const partial = (r: DailyRow) => r.on_min < 1400
  const at = hover != null ? rows[hover] : null
  const every = rows.length <= 14 ? 1 : Math.ceil(rows.length / 10)

  return (
    <Card title="Runtime per day"
      caption="Compressor time per local day, integrated from the gap between
               consecutive samples rather than counted. Paler columns are days
               the poller did not cover end to end — their totals are real but
               not comparable with a full day."
      legend={<>
        <span><i className="sw s1" />Cooling</span>
        {rows.some((r) => r.heat > 0) && <span><i className="sw s2" />Heating</span>}
        {rows.some(partial) && <span><i className="sw s1 faded" />Partial day</span>}
      </>}
      table={() => (
        <table>
          <thead><tr><th>Day</th><th className="num">Cooling</th>
            <th className="num">Heating</th><th className="num">Blower alone</th>
            <th className="num">Covered</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.day}>
                <td>{r.day}</td>
                <td className="num">{hhmm(r.cool)}</td>
                <td className="num">{r.heat ? hhmm(r.heat) : '—'}</td>
                <td className="num">{r.fan ? hhmm(r.fan) : '—'}</td>
                <td className="num">{hhmm(r.on_min)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}>
      <div ref={box} className="plot">
        <svg width="100%" height={height} role="img" className="focusable"
             aria-label="Compressor runtime per day. Use the arrow keys to step through the days."
             {...keyNav(rows.length, hover, setHover)}
             onMouseLeave={() => setHover(null)}>
          <YGrid ticks={ticks} y={yOf} x0={pad.l} x1={pad.l + iw}
                 fmt={(v) => (v >= 60 && v % 60 === 0 ? `${v / 60}h` : `${Math.round(v)}m`)} />

          {rows.map((r, i) => {
            const op = partial(r) ? 0.4 : 1
            const heatTop = yOf(r.heat)
            const coolTop = yOf(r.heat + r.cool)
            return (
              <g key={r.day} onMouseEnter={() => setHover(i)}>
                {/* Full-height hit target: a thin column is not pointable. */}
                <rect x={cx(i) - slot / 2} y={pad.t} width={slot} height={ih} fill="transparent" />
                {hover === i && (
                  <rect x={cx(i) - slot / 2 + 1} y={pad.t} width={slot - 2} height={ih}
                        fill="var(--surface-hi)" rx={4} />
                )}
                {r.heat > 0 && (
                  <path d={column(cx(i) - bw / 2, heatTop, bw, pad.t + ih - heatTop,
                                  r.cool > 0 ? 0 : 3)}
                        fill="var(--s2)" opacity={op} />
                )}
                {r.cool > 0 && (
                  // 2px of surface above the heating segment so the two read as
                  // separate quantities rather than one bar with a colour change.
                  <path d={column(cx(i) - bw / 2, coolTop, bw,
                                  heatTop - coolTop - (r.heat > 0 ? 2 : 0))}
                        fill="var(--s1)" opacity={op} />
                )}
              </g>
            )
          })}

          {rows.map((r, i) => i % every === 0 && (
            <text key={r.day} x={cx(i)} y={height - 8} textAnchor="middle" className="tick">
              {new Date(`${r.day}T12:00:00`).toLocaleDateString(undefined,
                { month: 'short', day: 'numeric' })}
            </text>
          ))}
        </svg>

        {at && (
          <Tip x={cx(hover!)} w={w} top={pad.t}>
            <div className="muted">{at.day}</div>
            <TipRow sw="s1" label="Cooling" value={hhmm(at.cool)} />
            {at.heat > 0 && <TipRow sw="s2" label="Heating" value={hhmm(at.heat)} />}
            {at.fan > 0 && <TipRow label="Blower alone" value={hhmm(at.fan)} />}
            <div className="muted" style={{ marginTop: 4 }}>
              {partial(at)
                ? `partial day — ${Math.round(at.on_min)} of 1440 min logged`
                : 'full day covered'}
            </div>
          </Tip>
        )}
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------- psychrometric

export interface Psychro {
  p_atm: number
  elevation_m: number
  dew_hi: number
  dew_lo: number
  w_hi: number
  w_lo: number
  curves: { rh: number; pts: [number, number][] }[]
}

/**
 * The psychrometric chart: every sample placed by dry-bulb temperature and
 * humidity ratio, over the constant-RH curves.
 *
 * It is the one picture where temperature and moisture are read together
 * without a second axis, because they are the two coordinates of a single
 * state. Cooling that only removes heat moves a point straight left; cooling
 * that dries moves it down. The target dew band is horizontal because humidity
 * ratio *is* dew point — a point above the band is too humid at any
 * temperature, which is exactly what a thermostat reading only temperature
 * cannot see.
 */
export function PsychroChart({ rows, psy, conv, deg, bucketS }: {
  rows: Row[]; psy: Psychro; conv: (c: number) => number; deg: string; bucketS: number
}) {
  const box = useRef<HTMLDivElement>(null)
  const clip = useId()
  const w = useWidth(box, rows.length)
  const [showOut, setShowOut] = useState(true)
  const [hover, setHover] = useState<number | null>(null)

  const title = 'Psychrometric chart'
  const kinds = [
    { k: 'in', label: 'Indoor', slot: 1, show: true },
    { k: 'out', label: 'Outdoor', slot: 2, show: showOut },
  ] as const
  const pts = kinds.flatMap((s) => !s.show ? [] : rows.flatMap((r, i) => {
    const t = r[`${s.k}_t`], wv = r[`${s.k}_w`]
    return t == null || wv == null ? [] : [{
      kind: s, i, t, w: wv, rh: r[`${s.k}_h`], dew: r[`${s.k}_d`], at: r.t,
    }]
  }))
  if (!pts.filter((p) => p.kind.k === 'in').length) return <Empty title={title} />

  const height = 340
  const pad = { l: 52, r: 18, t: 20, b: 40 }
  const iw = Math.max(120, w - pad.l - pad.r)
  const ih = height - pad.t - pad.b

  // Domain in display units, so the ticks land on round Fahrenheit too.
  const txs = pts.map((p) => conv(p.t))
  let x0 = Math.min(...txs), x1 = Math.max(...txs)
  const minSpan = deg === '°F' ? 14 : 8
  if (x1 - x0 < minSpan) { const m = (x0 + x1) / 2; x0 = m - minSpan / 2; x1 = m + minSpan / 2 }
  const xPad = (x1 - x0) * 0.06
  const xticks = niceTicks(x0 - xPad, x1 + xPad, 7)
  x0 = Math.min(x0 - xPad, xticks[0]); x1 = Math.max(x1 + xPad, xticks[xticks.length - 1])
  const wMax = Math.max(...pts.map((p) => p.w), psy.w_hi) * 1.15
  const yticks = niceTicks(0, wMax)
  const y1 = Math.max(wMax, yticks[yticks.length - 1])

  const X = (dispT: number) => pad.l + ((dispT - x0) / (x1 - x0)) * iw
  const Y = (gkg: number) => pad.t + ih - (gkg / y1) * ih

  const sat = psy.curves.find((c) => c.rh === 100)?.pts ?? []
  const satAt = (c: number) => {
    for (let k = 1; k < sat.length; k++) {
      if (sat[k][0] >= c) {
        const [ta, wa] = sat[k - 1], [tb, wb] = sat[k]
        return wa + ((c - ta) / (tb - ta)) * (wb - wa)
      }
    }
    return Infinity
  }
  const inDomain = (pts: [number, number][]) =>
    pts.filter(([c]) => conv(c) >= x0 - 1 && conv(c) <= x1 + 1)
  const line = (pts: [number, number][]) =>
    pts.map(([c, g], k) => `${k ? 'L' : 'M'}${X(conv(c)).toFixed(1)} ${Y(g).toFixed(1)}`).join(' ')

  const satPts = inDomain(sat)
  // Above the saturation curve air would be holding more water than it can:
  // shaded so the reader sees the edge of the chart is physics, not data.
  const impossible = satPts.length
    ? `${line(satPts)} L${X(x1)} ${pad.t} L${X(x0)} ${pad.t} Z` : ''
  // The target band, capped by saturation where it meets the curve.
  const band = satPts.length ? (() => {
    const top = satPts.map(([c]) => [c, Math.min(psy.w_hi, satAt(c))] as [number, number])
    const bot = satPts.map(([c]) => [c, Math.min(psy.w_lo, satAt(c))] as [number, number])
    return `${line(top)} ${bot.reverse().map(([c, g]) =>
      `L${X(conv(c)).toFixed(1)} ${Y(g).toFixed(1)}`).join(' ')} Z`
  })() : ''

  /** Where a curve leaves the plot: the right edge or the top, whichever first. */
  const curveEnd = (pts: [number, number][]) => {
    const vis = pts.filter(([c, g]) => conv(c) <= x1 && g <= y1)
    return vis.length ? vis[vis.length - 1] : null
  }

  const latest = kinds.map((s) => [...pts].reverse().find((p) => p.kind.k === s.k))
    .filter(Boolean) as typeof pts
  const at = hover != null ? pts[hover] : null
  const fmtT = (c: number | null) => (c == null ? '—' : `${conv(c).toFixed(1)}${deg}`)

  const byTime = new Map<number, { in?: (typeof pts)[number]; out?: (typeof pts)[number] }>()
  pts.forEach((p) => {
    const e = byTime.get(p.i) ?? {}
    e[p.kind.k] = p
    byTime.set(p.i, e)
  })

  return (
    <Card title={title}
      caption={<>
        Each dot is one {bucketS >= 3600 ? `${bucketS / 3600}-hour` : `${bucketS / 60}-minute`}
        {' '}average, placed by temperature (across) and moisture content (up). Curves
        are constant relative humidity. The shaded band is this zone&rsquo;s target dew
        point, {fmtT(psy.dew_lo)}–{fmtT(psy.dew_hi)}: dots above it are too humid at
        any temperature. Cooling that only chills moves a dot left; cooling that dries
        moves it down.
      </>}
      legend={<>
        <span><i className="dot s1" />Indoor</span>
        <button className={`lgbtn ${showOut ? '' : 'off'}`} aria-pressed={showOut}
                onClick={() => { setShowOut(!showOut); setHover(null) }}>
          <i className="dot s2" />Outdoor
        </button>
        <span><i className="dot ring" />Latest</span>
        <span><i className="sw band" />Target dew band</span>
        <span><i className="sw bare" />Relative humidity</span>
      </>}
      table={() => (
        <table>
          <thead><tr><th>Time</th>
            <th className="num">In temp</th><th className="num">In RH</th>
            <th className="num">In dew</th><th className="num">In g/kg</th>
            <th className="num">Out temp</th><th className="num">Out RH</th>
            <th className="num">Out g/kg</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="muted">{clock(r.t)}</td>
                <td className="num">{fmtT(r.in_t)}</td>
                <td className="num">{r.in_h == null ? '—' : `${r.in_h.toFixed(0)}%`}</td>
                <td className="num">{fmtT(r.in_d)}</td>
                <td className="num">{r.in_w?.toFixed(1) ?? '—'}</td>
                <td className="num">{fmtT(r.out_t)}</td>
                <td className="num">{r.out_h == null ? '—' : `${r.out_h.toFixed(0)}%`}</td>
                <td className="num">{r.out_w?.toFixed(1) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}>
      <div ref={box} className="plot">
        <svg width="100%" height={height} role="img"
             aria-label={`${title}: indoor${showOut ? ' and outdoor' : ''} conditions `
               + 'by dry-bulb temperature and humidity ratio. The table view lists every point.'}
             onMouseLeave={() => setHover(null)}
             onMouseMove={(e) => {
               const r = e.currentTarget.getBoundingClientRect()
               const mx = e.clientX - r.left, my = e.clientY - r.top
               let best = -1, bd = 26 * 26
               pts.forEach((p, k) => {
                 const dx = X(conv(p.t)) - mx, dy = Y(p.w) - my
                 const d = dx * dx + dy * dy
                 // Indoor wins ties: it is the series the chart is about.
                 if (d < bd || (d === bd && p.kind.k === 'in')) { bd = d; best = k }
               })
               setHover(best < 0 ? null : best)
             }}>
          <defs>
            <clipPath id={clip}>
              <rect x={pad.l} y={pad.t} width={iw} height={ih} />
            </clipPath>
          </defs>

          <YGrid ticks={yticks} y={Y} x0={pad.l} x1={pad.l + iw}
                 fmt={(v) => v.toFixed(tickDigits(yticks))} />
          {xticks.map((v) => (
            <g key={v}>
              <line x1={X(v)} x2={X(v)} y1={pad.t} y2={pad.t + ih}
                    stroke="var(--border)" strokeWidth={1} />
              <text x={X(v)} y={pad.t + ih + 16} textAnchor="middle" className="tick">
                {v.toFixed(0)}{deg}
              </text>
            </g>
          ))}
          <text x={pad.l} y={pad.t - 8} className="tick">moisture, g/kg</text>
          <text x={pad.l + iw} y={height - 4} textAnchor="end" className="tick">
            dry-bulb temperature
          </text>

          <g clipPath={`url(#${clip})`}>
            {impossible && <path d={impossible} fill="var(--surface-hi)" opacity={0.6} />}
            {band && <path d={band} fill="var(--text)" opacity={0.07} />}
            <line x1={pad.l} x2={pad.l + iw} y1={Y(psy.w_hi)} y2={Y(psy.w_hi)}
                  stroke="var(--dim)" strokeWidth={1} strokeDasharray="5 4" />
            <line x1={pad.l} x2={pad.l + iw} y1={Y(psy.w_lo)} y2={Y(psy.w_lo)}
                  stroke="var(--border-hi)" strokeWidth={1} />

            {psy.curves.map((c) => (
              <path key={c.rh} d={line(inDomain(c.pts))} fill="none"
                    stroke={c.rh === 100 ? 'var(--faint)' : 'var(--border-hi)'}
                    strokeWidth={c.rh === 100 ? 1.5 : 1} />
            ))}

            {pts.filter((p) => p.kind.k === 'out').map((p, k) => (
              <circle key={`o${k}`} cx={X(conv(p.t))} cy={Y(p.w)} r={3}
                      fill="var(--s2)" opacity={0.5} />
            ))}
            {pts.filter((p) => p.kind.k === 'in').map((p, k) => (
              <circle key={`i${k}`} cx={X(conv(p.t))} cy={Y(p.w)} r={3}
                      fill="var(--s1)" opacity={0.65} />
            ))}
          </g>

          {psy.curves.map((c) => {
            const e = curveEnd(inDomain(c.pts))
            return e && (
              <text key={c.rh} x={X(conv(e[0])) - 3} y={Y(e[1]) + (e[1] >= y1 * 0.97 ? 12 : -4)}
                    textAnchor="end" className="tick halo">{c.rh}%</text>
            )
          })}
          <text x={pad.l + 6} y={Y(psy.w_hi) - 5} className="tick halo">
            dew ceiling {fmtT(psy.dew_hi)}
          </text>

          {latest.map((p) => (
            <g key={p.kind.k}>
              <circle cx={X(conv(p.t))} cy={Y(p.w)} r={6} fill={`var(--s${p.kind.slot})`}
                      stroke="var(--surface)" strokeWidth={2.5} />
              <text x={X(conv(p.t)) + 10} y={Y(p.w) + 4} className="dlabel halo">
                {p.kind.label.toLowerCase()} now
              </text>
            </g>
          ))}

          {at && (
            <circle cx={X(conv(at.t))} cy={Y(at.w)} r={6} fill="none"
                    stroke="var(--text)" strokeWidth={1.5} />
          )}
        </svg>

        {at && (
          <Tip x={X(conv(at.t))} w={w} top={pad.t}>
            <div className="muted">{clock(at.at)}</div>
            {(['in', 'out'] as const).map((k) => {
              const p = byTime.get(at.i)?.[k]
              if (!p) return null
              return (
                <div key={k} style={{ marginTop: 6 }}>
                  <TipRow sw={`s${p.kind.slot}`} label={p.kind.label} value={fmtT(p.t)} />
                  <TipRow label="RH" value={p.rh == null ? '—' : `${p.rh.toFixed(0)}%`} />
                  <TipRow label="Dew point" value={fmtT(p.dew)} />
                  <TipRow label="Moisture" value={`${p.w.toFixed(1)} g/kg`} />
                </div>
              )
            })}
          </Tip>
        )}
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------- latent ramp

export interface Run {
  started: string
  minutes: number
  dew_drop: number | null
  temp_drop: number | null
}

/**
 * Dew removed against run length — the latent ramp, measured rather than fitted.
 *
 * The shape is the claim: near-zero removal for the first several minutes while
 * the coil cools to below the air's dew point, then a roughly linear slope once
 * it is condensing. Where that knee sits is the single most consequential number
 * this system learns, because it sets the minimum run length below which cooling
 * is pure waste.
 */
export function RampChart({ rows, deg }: { rows: Run[]; deg: string }) {
  const box = useRef<HTMLDivElement>(null)
  const w = useWidth(box, rows.length)
  const [hover, setHover] = useState<number | null>(null)

  const title = 'Latent ramp — dew removed vs run length'
  const pts = rows.filter((r) => r.dew_drop != null && r.minutes != null)
  if (pts.length < 3) return null

  const dc = deltaConv(deg)
  const height = 260
  const pad = { l: 52, r: 18, t: 20, b: 40 }
  const iw = Math.max(120, w - pad.l - pad.r)
  const ih = height - pad.t - pad.b

  const xticks = niceTicks(0, Math.max(30, ...pts.map((p) => p.minutes)), 6)
  const xMax = Math.max(xticks[xticks.length - 1], ...pts.map((p) => p.minutes))
  const ys = pts.map((p) => dc(p.dew_drop as number))
  const span = Math.max(0.3, Math.max(...ys) - Math.min(0, ...ys))
  const yticks = niceTicks(Math.min(0, ...ys) - span * 0.08, Math.max(...ys) + span * 0.08)
  const ylo = Math.min(yticks[0], ...ys), yhi = Math.max(yticks[yticks.length - 1], ...ys)

  const X = (m: number) => pad.l + (m / xMax) * iw
  const Y = (v: number) => pad.t + ih - ((v - ylo) / (yhi - ylo || 1)) * ih
  const fmt = (v: number | null) => (v == null ? '—' : `${dc(v).toFixed(2)}${deg}`)
  const at = hover != null ? pts[hover] : null

  return (
    <Card title={title}
      caption="One dot per compressor run. Runs near zero or below it cooled without
               drying — the coil had not yet got cold enough to condense water, and a
               run below the line ended wetter than it started. The knee where dots
               start to climb is how long this equipment needs before it dehumidifies."
      table={() => (
        <table>
          <thead><tr><th>Started</th><th className="num">Minutes</th>
            <th className="num">Dew removed</th><th className="num">Temp removed</th></tr></thead>
          <tbody>
            {pts.map((p, i) => (
              <tr key={i}>
                <td className="muted">{clock(p.started)}</td>
                <td className="num">{p.minutes.toFixed(0)}</td>
                <td className="num">{fmt(p.dew_drop)}</td>
                <td className="num">{fmt(p.temp_drop)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}>
      <div ref={box} className="plot">
        <svg width="100%" height={height} role="img"
             aria-label={`${title}. ${pts.length} runs. The table view lists each run.`}
             onMouseLeave={() => setHover(null)}
             onMouseMove={(e) => {
               const r = e.currentTarget.getBoundingClientRect()
               const mx = e.clientX - r.left, my = e.clientY - r.top
               let best = -1, bd = 26 * 26
               pts.forEach((p, k) => {
                 const d = (X(p.minutes) - mx) ** 2 + (Y(dc(p.dew_drop as number)) - my) ** 2
                 if (d < bd) { bd = d; best = k }
               })
               setHover(best < 0 ? null : best)
             }}>
          <YGrid ticks={yticks} y={Y} x0={pad.l} x1={pad.l + iw}
                 fmt={(v) => `${v.toFixed(tickDigits(yticks))}${deg}`} />
          {xticks.map((v) => (
            <text key={v} x={X(v)} y={pad.t + ih + 16} textAnchor="middle" className="tick">
              {v}m
            </text>
          ))}
          <text x={pad.l} y={pad.t - 8} className="tick">dew removed</text>
          <text x={pad.l + iw} y={height - 4} textAnchor="end" className="tick">run length</text>

          {/* Zero is the line that matters: below it a run ended wetter. */}
          <line x1={pad.l} x2={pad.l + iw} y1={Y(0)} y2={Y(0)}
                stroke="var(--faint)" strokeWidth={1.5} />
          {/* The short-cycle limit the Cycles panel counts against. */}
          {X(10) < pad.l + iw && (
            <>
              <rect x={pad.l} y={pad.t} width={X(10) - pad.l} height={ih}
                    fill="var(--surface-hi)" opacity={0.7} />
              <text x={X(10) - 4} y={pad.t + 12} textAnchor="end" className="tick">
                under 10 min
              </text>
            </>
          )}

          {pts.map((p, i) => (
            <circle key={i} cx={X(p.minutes)} cy={Y(dc(p.dew_drop as number))} r={4.5}
                    fill="var(--s1)" stroke="var(--surface)" strokeWidth={1.5}
                    opacity={hover == null || hover === i ? 0.9 : 0.45} />
          ))}
          {at && (
            <circle cx={X(at.minutes)} cy={Y(dc(at.dew_drop as number))} r={7}
                    fill="none" stroke="var(--text)" strokeWidth={1.5} />
          )}
        </svg>
        {at && (
          <Tip x={X(at.minutes)} w={w} top={pad.t}>
            <div className="muted">{clock(at.started)}</div>
            <TipRow sw="s1" label="Run length" value={`${at.minutes.toFixed(0)} min`} />
            <TipRow label="Dew removed" value={fmt(at.dew_drop)} />
            <TipRow label="Temp removed" value={fmt(at.temp_drop)} />
          </Tip>
        )}
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------- daily rhythm

export interface HourRow {
  hour: number
  dew: number | null
  temp: number | null
  rh: number | null
  duty: number | null
}

const hourLabel = (h: number) =>
  h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`

/**
 * Moisture has a daily shape because people make it.
 *
 * Two small multiples on one hour axis — dew point above, cooling duty below —
 * rather than one chart with two y-scales. Read down a column to see whether
 * the equipment answers the evening peak or sleeps through it.
 */
export function RhythmChart({ rows, conv, deg }: {
  rows: HourRow[]; conv: (c: number) => number; deg: string
}) {
  const box = useRef<HTMLDivElement>(null)
  const w = useWidth(box, rows.length)
  const [hover, setHover] = useState<number | null>(null)

  const pts = rows.filter((r) => r.dew != null)
  if (pts.length < 6) return null
  const byHour = new Map(rows.map((r) => [r.hour, r]))

  const pad = { l: 52, r: 18, t: 26 }
  const h1 = 140, gap = 40, h2 = 70, xb = 24
  const height = pad.t + h1 + gap + h2 + xb
  const iw = Math.max(120, w - pad.l - pad.r)
  const slot = iw / 24
  const cx = (h: number) => pad.l + slot * h + slot / 2

  const dews = pts.map((p) => conv(p.dew as number))
  const lo = Math.min(...dews), hi = Math.max(...dews)
  const yt = niceTicks(lo - Math.max(0.3, (hi - lo) * 0.15), hi + Math.max(0.3, (hi - lo) * 0.15), 4)
  const ylo = Math.min(yt[0], lo), yhi = Math.max(yt[yt.length - 1], hi)
  const Y = (v: number) => pad.t + h1 - ((v - ylo) / (yhi - ylo || 1)) * h1
  const b0 = pad.t + h1 + gap
  const dmax = Math.max(25, ...rows.map((r) => r.duty ?? 0))
  const dt = niceTicks(0, dmax, 2)
  const dTop = dt[dt.length - 1]
  const D = (v: number) => b0 + h2 - (v / dTop) * h2
  const bw = Math.max(3, Math.min(18, slot * 0.6))

  let d = ''
  let pen = false
  for (let h = 0; h < 24; h++) {
    const r = byHour.get(h)
    if (r?.dew == null) { pen = false; continue }
    d += `${pen ? 'L' : 'M'}${cx(h).toFixed(1)} ${Y(conv(r.dew)).toFixed(1)} `
    pen = true
  }
  const at = hover != null ? byHour.get(hover) : null

  return (
    <Card title="Daily rhythm by local hour"
      caption={<>Averaged over the window. Indoor dew point ranges {lo.toFixed(1)}–{hi.toFixed(1)}{deg} across the day. A pronounced evening
        peak is showers and cooking; a flat profile means the moisture is coming
        through the envelope rather than from the household.</>}
      legend={<>
        <span><i className="sw s1" />Indoor dew point</span>
        <span><i className="sw act" />Cooling duty</span>
      </>}
      table={() => (
        <table>
          <thead><tr><th>Hour</th><th className="num">Dew point</th>
            <th className="num">Temp</th><th className="num">RH</th>
            <th className="num">Cooling duty</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.hour}>
                <td>{hourLabel(r.hour)}</td>
                <td className="num">{r.dew == null ? '—' : `${conv(r.dew).toFixed(1)}${deg}`}</td>
                <td className="num">{r.temp == null ? '—' : `${conv(r.temp).toFixed(1)}${deg}`}</td>
                <td className="num">{r.rh == null ? '—' : `${r.rh.toFixed(0)}%`}</td>
                <td className="num">{r.duty == null ? '—' : `${r.duty.toFixed(0)}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}>
      <div ref={box} className="plot">
        <svg width="100%" height={height} role="img" className="focusable"
             aria-label="Indoor dew point and cooling duty by local hour. Use the arrow keys to step through the hours."
             {...keyNav(24, hover, setHover)}
             onMouseLeave={() => setHover(null)}
             onMouseMove={(e) => {
               const r = e.currentTarget.getBoundingClientRect()
               const h = Math.floor((e.clientX - r.left - pad.l) / slot)
               setHover(h >= 0 && h < 24 ? h : null)
             }}>
          {hover != null && (
            <rect x={cx(hover) - slot / 2 + 1} y={pad.t} width={slot - 2}
                  height={b0 + h2 - pad.t} fill="var(--surface-hi)" rx={4} />
          )}

          <text x={pad.l} y={pad.t - 14} className="tick">indoor dew point</text>
          <YGrid ticks={yt} y={Y} x0={pad.l} x1={pad.l + iw}
                 fmt={(v) => `${v.toFixed(tickDigits(yt))}${deg}`} />
          <path d={d.trim()} fill="none" stroke="var(--s1)" strokeWidth={2}
                strokeLinejoin="round" strokeLinecap="round" />
          {pts.map((p) => (
            <circle key={p.hour} cx={cx(p.hour)} cy={Y(conv(p.dew as number))}
                    r={hover === p.hour ? 5 : 3} fill="var(--s1)"
                    stroke="var(--surface)" strokeWidth={2} />
          ))}

          <text x={pad.l} y={b0 - 12} className="tick">cooling duty</text>
          <YGrid ticks={dt} y={D} x0={pad.l} x1={pad.l + iw} fmt={(v) => `${v}%`} />
          {rows.map((r) => (r.duty ?? 0) > 0 && (
            <path key={r.hour} d={column(cx(r.hour) - bw / 2, D(r.duty as number), bw,
                                         b0 + h2 - D(r.duty as number))}
                  fill="var(--dim)" />
          ))}

          {[0, 3, 6, 9, 12, 15, 18, 21].map((h) => (
            <text key={h} x={cx(h)} y={height - 6} textAnchor="middle" className="tick">
              {hourLabel(h)}
            </text>
          ))}
        </svg>
        {at && hover != null && (
          <Tip x={cx(hover)} w={w} top={pad.t}>
            <div className="muted">{hourLabel(hover)}–{hourLabel((hover + 1) % 24)}</div>
            <TipRow sw="s1" label="Dew point"
                    value={at.dew == null ? '—' : `${conv(at.dew).toFixed(1)}${deg}`} />
            <TipRow sw="act" label="Cooling duty"
                    value={at.duty == null ? '—' : `${at.duty.toFixed(0)}%`} />
            <TipRow label="Temp" value={at.temp == null ? '—' : `${conv(at.temp).toFixed(1)}${deg}`} />
          </Tip>
        )}
      </div>
    </Card>
  )
}
